/* Copyright (C) Okahu Inc 2023-2024. All rights reserved. */

'use client';

import { Dispatch, SetStateAction, useEffect } from 'react';
import { useLanguage } from '@/providers/LanguageProvider';
import { EvaluateTemplate } from '@/ux-common/src/types/api';
import { LucidePlus } from 'lucide-react';
import { CriteriaPayloadType } from 'types/prompt';

import { cn } from '@/lib/utils';
import { useEvals } from '@/hooks/useEvals';
import {
  CriteriaDataItem,
  TreeDropdownCheckbox,
} from '@/components/treeview-dropdown/tree-dropdown-checkbox';

import { CustomButton } from '../common';

type EvalCriteriaFilterProps = {
  inferTitle: 'inferences' | 'traces' | 'conversations';
  setCriteriaPayload: Dispatch<SetStateAction<CriteriaPayloadType>>;
  selectedIds: string[];
  setSelectedIds: Dispatch<SetStateAction<string[]>>;
  containerVisible: boolean;
  setContainerVisible: Dispatch<SetStateAction<boolean>>;
  fetchTemplates?: boolean;
  appName?: string;
  startTime?: string;
  endTime?: string;
  workflowName?: string;
};

const EvalCriteriaFilter = ({
  inferTitle,
  setCriteriaPayload,
  selectedIds,
  setSelectedIds,
  containerVisible,
  setContainerVisible,
  fetchTemplates = true,
  appName,
  startTime,
  endTime,
  workflowName,
}: EvalCriteriaFilterProps) => {
  const { messages } = useLanguage();
  const langData = messages?.Evaluation;

  const {
    state: { templateLoading, evalTemplates },
    evaluateActions: { fetchEvalsTemplate },
  } = useEvals();

  const transformActualData: CriteriaDataItem[] = evalTemplates
    ?.filter((template: any) => !template.category?.includes('test'))
    .sort((first: EvaluateTemplate, second: EvaluateTemplate) =>
      first.name.localeCompare(second.name)
    )
    .map((item: EvaluateTemplate, id: number) => {
      const { name, eval_result_schema } = item;
      return {
        id: `${name}_${id}`,
        name: name,
        children:
          eval_result_schema &&
          eval_result_schema.label &&
          Array.isArray(eval_result_schema.label.enums)
            ? eval_result_schema.label.enums.map((enumItem: string) => ({
                id: `parent_${name}_${enumItem}`,
                name: enumItem,
              }))
            : [],
      };
    });

  // Returns summary for each parent based on selectedIds
  function getSelectionSummaries(): string[] {
    return transformActualData
      ?.map((item) => {
        // If no children, include parent if selected
        if (!item.children || item.children.length === 0) {
          return selectedIds.includes(item.id) ? item.name : null;
        }
        const selectedChildren = item.children.filter((child) =>
          selectedIds.includes(child.id)
        );
        if (selectedChildren.length === item.children.length) {
          return item.name;
        } else if (selectedChildren.length > 0) {
          return `${item.name}:${selectedChildren.map((child) => child.name).join(',')}`;
        }
        return null;
      })
      .filter((val): val is string => typeof val === 'string');
  }

  useEffect(() => {
    if (containerVisible && fetchTemplates) {
      fetchEvalsTemplate(inferTitle, appName, startTime, endTime, workflowName);
    }
  }, [
    containerVisible,
    fetchTemplates,
    inferTitle,
    appName,
    startTime,
    endTime,
    workflowName,
  ]);

  useEffect(() => {
    setCriteriaPayload((prev) => {
      const summaries = getSelectionSummaries();
      return {
        ...prev,
        currentState: summaries || [],
      };
    });
  }, [selectedIds, evalTemplates]);

  return (
    <div>
      <p className="mb-2 flex items-center gap-1">
        <span className="text-form-input-text text-body-primary">
          {langData?.evaluation}
        </span>
      </p>
      <CustomButton
        customVariant="secondary"
        customSize="large"
        icon={
          <LucidePlus
            className="text-[#C1C9CF] hover:text-[#40565A]"
            size={18}
          />
        }
        iconPosition="left"
        className={cn('w-full gap-0.5', containerVisible && 'hidden')}
        onClick={() => setContainerVisible(true)}
      >
        {langData?.add_evals_criteria}
      </CustomButton>
      {containerVisible && (
        <TreeDropdownCheckbox
          data={transformActualData}
          selectedIds={selectedIds}
          setSelectedIds={setSelectedIds}
          loading={templateLoading}
        />
      )}
    </div>
  );
};

export default EvalCriteriaFilter;
